import {
  Alert,
  Box,
  Header,
  Link,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@cloudscape-design/components';
import { useNavigate } from 'react-router';
import type { RecentRuleEvaluation } from '@amzn/helianthus-arbiter-client';
import { useRecentRuleEvaluations } from '../../hooks/useRecentRuleEvaluations';
import { useAutoPagedCollection } from '../../hooks/useAutoPagedCollection';
import { EvaluationIdCell } from '../../components/EvaluationIdCell';
import { infoPopover } from '../../components/InfoPopover';
import { formatDateTime } from '../../utils/formatFieldName';
import { buildRuleId, buildRuleUrl } from '../../utils/evaluationHelpers';

/**
 * Rows per page. Deliberately smaller than one fetched batch, so arriving at the last page usually
 * means one more batch is fetched while there is still a page left to read.
 */
const PAGE_SIZE = 25;

/** A muted dash reads differently from a missing value. */
const DASH = <Box color="text-status-inactive">—</Box>;

const DRY_RUN_FINDINGS_INFO =
  'The rule ran as a dry run here. Its findings were not reviewed by the validation judge and did not affect the evaluation, so this count is not comparable with active runs.';

interface RecentEvaluationsTabProps {
  ruleName: string;
}

export function RecentEvaluationsTab({ ruleName }: RecentEvaluationsTabProps) {
  const navigate = useNavigate();
  const { evaluations, loading, fetchingMore, error, hasMore, loadMore, refetch } =
    useRecentRuleEvaluations(ruleName);

  const { items, collectionProps, paginationProps } = useAutoPagedCollection(
    evaluations,
    { pagination: { pageSize: PAGE_SIZE } },
    { hasMore, fetchingMore, loadMore },
  );

  return (
    <SpaceBetween size="l">
      {/* Above the table, not in its empty slot, so a failed next batch leaves the loaded rows alone.
          Retry resumes from the held token rather than discarding the history already read. */}
      {error && (
        <Alert
          type="error"
          header="Could not load recent evaluations"
          buttonText="Retry"
          onButtonClick={hasMore ? loadMore : refetch}
        >
          {error}
        </Alert>
      )}

      <Table
        {...collectionProps}
        items={items}
        loading={loading}
        loadingText="Loading recent evaluations..."
        trackBy="evaluationId"
        columnDefinitions={[
          {
            id: 'evaluationId',
            header: 'Evaluation ID',
            cell: (item: RecentRuleEvaluation) => (
              <EvaluationIdCell
                evaluationId={item.evaluationId!}
                ruleId={buildRuleId(ruleName, item.ruleVersion)}
                isDryRun={item.isDryRun}
              />
            ),
          },
          {
            id: 'lastEvaluatedAt',
            header: 'Last evaluated',
            cell: (item: RecentRuleEvaluation) => formatDateTime(item.lastEvaluatedAt),
          },
          {
            id: 'ruleVersion',
            header: 'Rule version',
            cell: (item: RecentRuleEvaluation) => {
              if (!item.ruleVersion) return DASH;
              // Carries no ?tab on purpose: this list is per rule, not per version, so following a
              // version means "show me that version", which is the Details tab.
              const href = buildRuleUrl(ruleName, item.ruleVersion);
              return (
                <Link
                  href={href}
                  onFollow={(event) => {
                    event.preventDefault();
                    navigate(href);
                  }}
                >
                  {item.ruleVersion}
                </Link>
              );
            },
          },
          {
            id: 'findingCount',
            header: 'Findings',
            cell: (item: RecentRuleEvaluation) =>
              item.isDryRun ? (
                <SpaceBetween direction="horizontal" size="xs">
                  <span>{item.findingCount ?? DASH}</span>
                  {infoPopover('Dry-run findings', DRY_RUN_FINDINGS_INFO)}
                </SpaceBetween>
              ) : (
                (item.findingCount ?? DASH)
              ),
          },
        ]}
        header={
          <Header
            variant="h2"
            // The trailing '+' says the count is a floor, as on the evaluations list.
            counter={
              loading ? undefined : `(${evaluations.length}${paginationProps.openEnd ? '+' : ''})`
            }
            // The 100 is the service's cap on one pagination sequence, not ours.
            description="The 100 most recent, newest first, across every version of the rule."
          >
            Recent evaluations
          </Header>
        }
        pagination={
          <Pagination
            {...paginationProps}
            ariaLabels={{
              paginationLabel: 'Recent evaluations pagination',
              previousPageLabel: 'Previous page',
              nextPageLabel: 'Next page',
              pageLabel: (pageNumber) => `Page ${pageNumber}`,
            }}
          />
        }
        empty={
          // Only when nothing is loaded at all: a failed fetch, or a reader paged past the loaded rows,
          // also empties this page, and "never ran" would then be false.
          error || evaluations.length > 0 ? null : (
            <Box textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <b>No evaluations</b>
                <Box variant="p" color="inherit">
                  This rule has not stored a result in any recorded evaluation.
                </Box>
              </SpaceBetween>
            </Box>
          )
        }
        footer={
          // Only while a background batch is in flight, so the table never looks stalled.
          fetchingMore ? (
            <Box textAlign="center">
              <StatusIndicator type="loading">Loading more evaluations</StatusIndicator>
            </Box>
          ) : undefined
        }
      />
    </SpaceBetween>
  );
}
